/* eslint-disable eslint-comments/no-restricted-disable, max-lines -- File is too complex to refactor right now. Should be handled later */
import { useCallback, useEffect, useMemo, useState } from "react";
import type { Dispatch, SetStateAction } from "react";
import { useFormContext } from "react-hook-form";
import type { UseMutationResult } from "react-query";
import { useHistory } from "react-router-dom";
import { DEFAULT_ERROR } from "@/react/components/Paywalls/Checkout/helpers";
import { getPreviewParameters } from "@/react/components/Paywalls/Checkout/helpers";
import { useCheckoutSelectedPrice } from "@/react/components/Paywalls/Checkout/hooks/useCheckoutSelectedPrice";
import type { CheckoutFormValuesProps } from "@/react/components/Paywalls/Checkout/types/CheckoutFormValuesProps";
import type { CheckoutProps } from "@/react/components/Paywalls/Checkout/types/CheckoutProps";
import type { PaywallCustomization } from "@/react/types/PaywallCustomization";
import { isValidTaxIdValue } from "@/react/utils/paywalls/taxId/helpers";
import {
  type PaywallPreviewCouponApplication,
  useCouponPreviewMutation,
} from "@circle-react/components/Paywalls/Checkout/hooks/useCouponPreviewMutation";
import {
  CouponApplicationStatus,
  findPrice,
  getCouponApplication,
} from "@circle-react/components/Paywalls/Checkout/hooks/useGetCouponApplication";
import { usePreviewMutation } from "@circle-react/components/Paywalls/Checkout/hooks/usePreviewMutation";
import { isActive } from "@circle-react/helpers/paywallHelpers";
import { useIsPaywallsCheckoutFixValidationEnabled } from "@circle-react/hooks/useIsPaywallsCheckoutFixIdValidationEnabled";
import type {
  Paywall,
  PaywallCheckoutPreview,
  PaywallFailure,
} from "@circle-react/types";
import type { PaywallPrice } from "@circle-react/types/PaywallPrice";
import { useCardInfo } from "./useCardInfo";
import { useMemberAddressAttributes } from "./useMemberAddressAttributes";
import { usePaywallCustomization } from "./usePaywallCustomization";

export interface PaymentError {
  disablePayButton: boolean;
  existingAccount?: boolean;
  message: string;
  type: string;
}

export const initialPaymentError: PaymentError = {
  disablePayButton: false,
  existingAccount: false,
  message: "",
  type: "",
};

export interface PaywallHandlerError extends Error {
  body: {
    failure: {
      category: string;
      code: string;
      level: string;
      message: string;
    };
  };
}

export interface PreviewError extends Error {
  body: {
    message: string;
    error_details?: {
      category: string;
      code: string;
    };
  };
}

export interface UsePaywallCheckoutProps {
  checkoutPreviewContext?: CheckoutProps["checkoutPreviewContext"];
  isMemberAddressRequired: boolean;
  isMemberWithSavedPaymentMethods: boolean;
  isTaxIdMandatory: boolean;
  loginUrl: string;
  paywall: Paywall;
  resetPasswordUrl: string;
}

export type PaywallCheckoutPreviewMutationResult =
  | UseMutationResult<PaywallCheckoutPreview, PreviewError, any>
  | Record<string, never>;

export interface UsePaywallCheckoutReturn {
  amountDue: number;
  applyCouponCode: (props: { data?: PaywallPreviewCouponApplication }) => void;
  clearAlerts: () => void;

  clearCouponCode: (prop: { resetPreview: boolean }) => void;
  couponPreviewMutation: ReturnType<typeof useCouponPreviewMutation>;
  enableTaxIdCollection: () => void;
  getSelectedPrice: () => PaywallPrice;
  handleError: (param: any) => void;
  isCardInfoComplete: boolean;
  isCardInfoRequired: boolean;
  isCardInfoValid: boolean;
  isMemberUsingSavedPaymentMethod: boolean;
  isPaywallActive: boolean;
  isPerformingVerification: boolean;
  isProcessingPayment: boolean;
  isSelectedPrice: (price: PaywallPrice) => boolean;
  isSetupIntentRequired: boolean;
  isTaxIdCollectionEnabled: boolean;
  loginRedirectUrl: string;
  paymentError: PaymentError;
  paywallCustomization?: PaywallCustomization;
  paywallFailure: PaywallFailure | null;
  previewMutation: PaywallCheckoutPreviewMutationResult;
  resetPasswordRedirectUrl: string;
  setIsCardInfoComplete: (arg: boolean) => void;
  setIsMemberUsingSavedPaymentMethod: (arg: boolean) => void;
  setIsPerformingVerification: (arg: boolean) => void;
  setIsProcessingPayment: (arg: boolean) => void;
  setMemberAddressAttributes: (event: any) => void;
  setPaymentError: Dispatch<SetStateAction<PaymentError>>;
  setPaywallFailure: Dispatch<SetStateAction<PaywallFailure | null>>;
  setSelectedPrice: (priceId: number) => void;

  // TODO: replace these props with a hook similar to `useCheckoutSelectedPrice` that relies on the `payment_method_type` form value.
  // We are using `stripePaymentMethodType` to set the `payment_method_type` prop when submitting
  // requests to our backend. This is unnecessary as we can derive the payment method type from the
  // form value.
  setStripePaymentMethodType: (arg: string) => void;
  stripePaymentMethodType: string;
}

export const usePaywallCheckout = ({
  isMemberAddressRequired,
  isTaxIdMandatory,
  paywall,
  isMemberWithSavedPaymentMethods,
  loginUrl,
  resetPasswordUrl,
  checkoutPreviewContext,
}: UsePaywallCheckoutProps): UsePaywallCheckoutReturn => {
  const { watch, setValue, setError, getValues, clearErrors } =
    useFormContext<CheckoutFormValuesProps>();

  const history = useHistory();

  const [isMemberUsingSavedPaymentMethod, setIsMemberUsingSavedPaymentMethod] =
    useState(false);

  const [stripePaymentMethodType, setStripePaymentMethodType] =
    useState("card");
  const [isCardInfoComplete, setIsCardInfoComplete] = useState(false);
  const [paymentError, setPaymentError] = useState(initialPaymentError);
  const [paywallFailure, setPaywallFailure] = useState<PaywallFailure | null>(
    null,
  );
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [amountDue, setAmountDue] = useState(0);
  const [isFreeForever, setIsFreeForever] = useState(false);

  const clearAlerts = () => {
    setPaymentError(initialPaymentError);
    setPaywallFailure(null);
  };

  useEffect(() => {
    if (paymentError.existingAccount) {
      setError("email", { type: "duplicated" });
    }
  }, [setError, paymentError]);

  const couponPreviewMutation = useCouponPreviewMutation({
    checkoutPreviewContext,
    paywall,
  });
  const previewMutation = usePreviewMutation({
    checkoutPreviewContext,
    paywall,
  });

  const {
    paywall_price_id,
    payment_method_type,
    community_member_billing_info_attributes,
  } = watch();

  const previewCheckout = useCallback(
    (couponPreviewData?: PaywallPreviewCouponApplication) => {
      const params: any = getPreviewParameters(getValues());

      const price = findPrice(
        paywall_price_id,
        couponPreviewData?.paywall?.prices ||
          couponPreviewMutation?.data?.paywall?.prices,
      );
      const couponApplication = getCouponApplication(
        price?.coupon_applications,
      );

      if (couponApplication.status !== CouponApplicationStatus.COUPON_APPLIED) {
        params.queryParams.coupon_code = "";
      }

      previewMutation.mutate(params, {
        onSuccess: preview => {
          setPaymentError(initialPaymentError);
          setAmountDue(preview.amount_due_now);
          setIsFreeForever(preview.free_forever);
        },
        onError: error => {
          console.error(error);

          if (error.body.error_details?.code?.match(/coupon_/)) {
            previewCheckout();
            return;
          }

          setPaymentError({
            message: error.message,
            type: "danger",
            disablePayButton: true,
          });
        },
      });
    },
    [
      setValue,
      setPaymentError,
      previewMutation,
      couponPreviewMutation,
      getValues,
      paywall_price_id,
    ],
  );

  useEffect(() => {
    previewCheckout();
  }, [
    paywall_price_id,
    payment_method_type,
    community_member_billing_info_attributes?.address_country,
  ]);

  const { getSelectedPrice, setSelectedPrice, isSelectedPrice } =
    useCheckoutSelectedPrice(paywall, previewCheckout);

  const setMemberAddressAttributes = useMemberAddressAttributes(
    watch,
    setValue,
    isMemberAddressRequired,
    previewCheckout,
  );

  const isPaywallsCheckoutFixValidationEnabled =
    useIsPaywallsCheckoutFixValidationEnabled();

  const applyCouponCode = useCallback(
    ({ data }: { data?: PaywallPreviewCouponApplication }) => {
      const code = data?.paywall_coupon?.code || watch().coupon_code || "";
      setValue("coupon_code", code.toUpperCase());

      previewCheckout(data);
      const searchParams = new URLSearchParams(history.location.search);
      const affiliateCode = searchParams.get("affiliate_code");

      if (!checkoutPreviewContext) {
        searchParams.set("coupon_code", code);

        if (affiliateCode) {
          searchParams.set("affiliate_code", affiliateCode);
        }
        history.replace({
          pathname: history.location.pathname,
          search: searchParams.toString(),
        });
      }
    },
    [watch, setValue, previewCheckout, history],
  );

  const clearCouponCode = useCallback(
    ({ resetPreview = true }) => {
      clearErrors("coupon_code");

      setValue("coupon_code", "");
      setIsFreeForever(false);

      if (resetPreview) {
        couponPreviewMutation.reset();
        previewCheckout();
      }
      const searchParams = new URLSearchParams(history.location.search);
      const affiliateCode = searchParams.get("affiliate_code");
      if (affiliateCode) {
        history.replace({
          pathname: history.location.pathname,
          search: `?affiliate_code=${affiliateCode}`,
        });
      } else {
        history.replace({
          pathname: history.location.pathname,
          search: "",
        });
      }
    },
    [setValue, previewCheckout, history, clearErrors, couponPreviewMutation],
  );

  const [isTaxIdCollectionEnabled, setTaxIdCollectionEnabled] =
    useState(isTaxIdMandatory);

  const enableTaxIdCollection = useCallback(
    () => setTaxIdCollectionEnabled(true),
    [setTaxIdCollectionEnabled],
  );

  const [taxIdDebounceTimer, setTaxIdDebounceTimer] = useState<any>();
  const clearTaxIdDebounceTimer = useCallback(() => {
    if (taxIdDebounceTimer) {
      clearTimeout(taxIdDebounceTimer);
      setTaxIdDebounceTimer(undefined);
    }
  }, [taxIdDebounceTimer, setTaxIdDebounceTimer]);
  useEffect(() => {
    const debouncePreviewCheckout = () => {
      clearTaxIdDebounceTimer();
      setTaxIdDebounceTimer(
        setTimeout(() => {
          previewCheckout();
        }, 250),
      );
    };

    const taxIdChangesSub = watch((values, { name }) => {
      if (name === "community_member_billing_info_attributes.tax_id_type") {
        const taxIdEnum =
          values["community_member_billing_info_attributes"]?.["tax_id_type"] ||
          "";

        if (taxIdEnum === "") {
          debouncePreviewCheckout();
        }
      } else if (
        name === "community_member_billing_info_attributes.tax_id_value"
      ) {
        const taxIdEnum =
          values["community_member_billing_info_attributes"]?.["tax_id_type"] ||
          "";
        const taxIdValue =
          values["community_member_billing_info_attributes"]?.[
            "tax_id_value"
          ] || "";

        if (
          isValidTaxIdValue({
            taxIdEnum,
            taxIdValue,
            countryCode:
              community_member_billing_info_attributes?.address_country,
            isPaywallsCheckoutFixValidationEnabled,
          })
        ) {
          debouncePreviewCheckout();
        }
      }
    });

    return () => {
      taxIdChangesSub.unsubscribe();
      clearTaxIdDebounceTimer();
    };
  }, [
    watch,
    previewCheckout,
    clearTaxIdDebounceTimer,
    setTaxIdDebounceTimer,
    clearAlerts,
  ]);

  const selectedPrice = getSelectedPrice();
  const isPaywallActive = isActive(paywall);

  const { isCardInfoRequired, isCardInfoValid, isSetupIntentRequired } =
    useCardInfo(
      paywall,
      selectedPrice,
      payment_method_type,
      isCardInfoComplete,
      isMemberWithSavedPaymentMethods,
      isMemberUsingSavedPaymentMethod,
      isFreeForever,
    );

  const handleError = (options: any) => {
    setPaymentError({ ...DEFAULT_ERROR, ...options });
  };

  const loginRedirectUrl = useMemo(() => {
    const hasEmailLoginOption = loginUrl.includes("#email");
    const baseUrl = loginUrl.replace("#email", "");
    const redirectUrl = `${baseUrl}?post_login_redirect=${encodeURIComponent(window.location.href)}`;
    return hasEmailLoginOption ? `${redirectUrl}#email` : redirectUrl;
  }, [loginUrl, window.location.href]);

  const resetPasswordRedirectUrl = useMemo(
    () =>
      `${resetPasswordUrl}?post_login_redirect=${encodeURIComponent(
        window.location.href,
      )}`,
    [resetPasswordUrl, window.location.href],
  );

  const paywallCustomization = usePaywallCustomization(
    paywall?.paywall_customization,
  );

  const [isPerformingVerification, setIsPerformingVerification] =
    useState<boolean>(false);

  return {
    clearAlerts,
    setSelectedPrice,
    getSelectedPrice,
    isSelectedPrice,
    applyCouponCode,
    clearCouponCode,
    enableTaxIdCollection,
    isTaxIdCollectionEnabled,
    paymentError,
    couponPreviewMutation,
    previewMutation,
    setMemberAddressAttributes,
    setPaymentError,
    isCardInfoComplete,
    setIsCardInfoComplete,
    isCardInfoValid,
    isPaywallActive,
    isCardInfoRequired,
    isSetupIntentRequired,
    handleError,
    isProcessingPayment,
    setIsProcessingPayment,
    amountDue,
    stripePaymentMethodType,
    setStripePaymentMethodType,
    isMemberUsingSavedPaymentMethod,
    setIsMemberUsingSavedPaymentMethod,
    paywallFailure,
    setPaywallFailure,
    loginRedirectUrl,
    resetPasswordRedirectUrl,
    paywallCustomization,
    isPerformingVerification,
    setIsPerformingVerification,
  };
};
